import React, { useState } from "react";
import { TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  uploadAttachment,
  updateAttachment,
} from "../../services/attachmentService";
import { updateTask } from "../../services/taskService";
import {
  updateProject,
  updateProjectTask,
} from "../../services/projectService";
import { Attachment } from "../../types";
import FileUploader from "../Common/FileUploader";
import FormDialog from "../Common/FormDialog";
import { extractErrorMessage } from "../../utils/apiHelpers";

interface Props {
  parentId: string;
  open: boolean;
  onClose: () => void;
  onCreated: (attachment: Attachment, file?: File) => Promise<void>;
  attachment?: Attachment | null;
  parentType?: "Task" | "Project" | "ProjectTask";
  existingAttachments?: Attachment[];
}

const AttachmentForm: React.FC<Props> = ({
  parentId,
  open,
  onClose,
  onCreated,
  attachment,
  parentType = "Task",
  existingAttachments = [],
}) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState(attachment?.name || "");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState("");

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setFileName(file.name);
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
    setFileName(attachment?.name || "");
  };

  const handleSave = async () => {
    if (!fileName) {
      setError(t("attachmentForm.fileNameRequired") || "Имя файла обязательно");
      return;
    }

    setUploading(true);
    setError("");
    try {
      if (attachment && attachment.id && !selectedFile) {
        const updatedAttachment = await updateAttachment(attachment.id, {
          name: fileName,
        });
        await onCreated(updatedAttachment);
      } else if (selectedFile) {
        const uploadedAttachment = await uploadAttachment(
          parentId,
          selectedFile,
          parentType,
        );

        if (parentId === "new") {
          const newAttachment: Attachment = {
            id: uploadedAttachment.id,
            name: fileName,
            url: uploadedAttachment.url,
            type: uploadedAttachment.type,
            size: uploadedAttachment.size,
            parentType: parentType,
            role: "Attachment",
          };
          const currentAttachments = JSON.parse(
            localStorage.getItem("pendingAttachments") || "[]",
          );
          localStorage.setItem(
            "pendingAttachments",
            JSON.stringify([...currentAttachments, newAttachment]),
          );
          await onCreated(newAttachment, selectedFile);
        } else {
          if (parentType === "Project") {
            // Для проектов: используем переданные вложения и обновляем проект
            await updateProject(parentId, {
              cAttachmentIds: [
                ...existingAttachments.map((a) => a.id),
                uploadedAttachment.id,
              ],
              cAttachmentNames: {
                ...existingAttachments.reduce(
                  (acc, a) => ({ ...acc, [a.id]: a.name }),
                  {},
                ),
                [uploadedAttachment.id]: fileName,
              },
              cAttachmentTypes: {
                ...existingAttachments.reduce(
                  (acc, a) => ({ ...acc, [a.id]: a.type || "" }),
                  {},
                ),
                [uploadedAttachment.id]: uploadedAttachment.type || "",
              },
            });
          } else if (parentType === "ProjectTask") {
            await updateProjectTask(parentId, {
              attachmentsIds: [
                ...existingAttachments.map((a) => a.id),
                uploadedAttachment.id,
              ],
              attachmentsNames: {
                ...existingAttachments.reduce(
                  (acc, a) => ({ ...acc, [a.id]: a.name }),
                  {},
                ),
                [uploadedAttachment.id]: fileName,
              },
              attachmentsTypes: {
                ...existingAttachments.reduce(
                  (acc, a) => ({ ...acc, [a.id]: a.type || "" }),
                  {},
                ),
                [uploadedAttachment.id]: uploadedAttachment.type || "",
              },
            });
          } else {
            await updateTask(parentId, {
              attachmentsIds: [
                ...existingAttachments.map((a) => a.id),
                uploadedAttachment.id,
              ],
              attachmentsNames: {
                ...existingAttachments.reduce(
                  (acc, a) => ({ ...acc, [a.id]: a.name }),
                  {},
                ),
                [uploadedAttachment.id]: fileName,
              },
              attachmentsTypes: {
                ...existingAttachments.reduce(
                  (acc, a) => ({ ...acc, [a.id]: a.type || "" }),
                  {},
                ),
                [uploadedAttachment.id]: uploadedAttachment.type || "",
              },
            });
          }
          await onCreated(uploadedAttachment, selectedFile);
        }
      }
      setFileName("");
      setSelectedFile(null);
      onClose();
    } catch (error: any) {
      console.error("Ошибка при сохранении файла:", error);
      const message = extractErrorMessage(
        error,
        t("attachmentForm.saveError") || "Ошибка при сохранении файла",
      );
      setError(message);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setFileName(attachment?.name || "");
    setError("");
    onClose();
  };

  return (
    <FormDialog
      open={open}
      title={
        attachment
          ? t("attachmentForm.editTitle") || "Редактировать вложение"
          : t("attachmentForm.title") || "Новое вложение"
      }
      onClose={handleClose}
      onSubmit={handleSave}
      submitLabel={
        attachment
          ? t("attachmentForm.saveButton") || "Сохранить"
          : t("attachmentForm.uploadButton") || "Загрузить"
      }
      cancelLabel={t("attachmentForm.cancelButton") || "Отмена"}
      loading={uploading}
      disabled={uploading || (!attachment && !selectedFile)}
    >
      {!attachment && (
        <FileUploader
          selectedFile={selectedFile}
          fileName={fileName}
          uploading={uploading}
          disabled={!!attachment}
          onFileSelect={handleFileSelect}
          onFileRemove={handleFileRemove}
        />
      )}
      {attachment && (
        <TextField
          autoFocus
          label={t("attachmentForm.fileNameLabel") || "Имя файла"}
          value={fileName}
          onChange={(e) => setFileName(e.target.value)}
          fullWidth
          error={!!error}
          helperText={error}
          disabled={uploading}
          sx={{ mb: 2 }}
        />
      )}
    </FormDialog>
  );
};

export default AttachmentForm;
